/**
 * WizardList - Reusable list component for wizard step 1 (item selection)
 *
 * Features:
 * - Generic item type support
 * - Two-line item display (title + subtitle)
 * - Hover and selected states
 * - Chevron indicator for navigation
 * - Empty state message
 *
 * @example
 * ```tsx
 * <WizardList
 *   items={groups}
 *   selectedId={selected?.id}
 *   onSelect={(group) => handleSelect(group)}
 *   getKey={(g) => g.id}
 *   getTitle={(g) => g.name}
 *   getSubtitle={(g) => `${g.members.length} accounts`}
 *   emptyMessage="No groups yet. Create a new group to get started."
 * />
 * ```
 */

import * as React from 'react'
import { cn } from '../../utils'

export interface WizardListItem {
  id: string | number
}

export interface WizardListProps<T extends WizardListItem> {
  /** List of items to display */
  items: T[]
  /** Currently selected item ID */
  selectedId?: string | number | null
  /** Callback when an item is selected */
  onSelect: (item: T) => void
  /** Extract unique key from item (defaults to item.id) */
  getKey?: (item: T) => string | number
  /** Extract title text from item */
  getTitle: (item: T) => React.ReactNode
  /** Extract subtitle text from item */
  getSubtitle?: (item: T) => React.ReactNode
  /** Message to display when list is empty */
  emptyMessage?: string
  /** Additional className for the list container */
  className?: string
}

const ChevronIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    className="text-muted-foreground flex-shrink-0"
  >
    <path
      d="M6 4l4 4-4 4"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

function WizardListInner<T extends WizardListItem>(
  {
    items,
    selectedId,
    onSelect,
    getKey,
    getTitle,
    getSubtitle,
    emptyMessage = 'No items available.',
    className,
  }: WizardListProps<T>,
  ref: React.ForwardedRef<HTMLDivElement>
) {
  const extractKey = getKey ?? ((item: T) => item.id)

  if (items.length === 0) {
    return (
      <div
        ref={ref}
        className={cn(
          'flex-1 px-6 flex items-center justify-center',
          className
        )}
      >
        <div className="text-sm text-muted-foreground text-center">
          {emptyMessage}
        </div>
      </div>
    )
  }

  return (
    <div
      ref={ref}
      className={cn('divide-y divide-border', className)}
    >
      {items.map((item) => {
        const key = extractKey(item)
        const isSelected = selectedId === key

        return (
          <button
            key={key}
            className={cn(
              'w-full text-left px-4 py-3 hover:bg-accent transition-colors focus:outline-none',
              isSelected && 'bg-secondary'
            )}
            onClick={() => onSelect(item)}
          >
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium truncate">
                  {getTitle(item)}
                </div>
                {getSubtitle && (
                  <div className="text-xs text-muted-foreground">
                    {getSubtitle(item)}
                  </div>
                )}
              </div>
              <ChevronIcon />
            </div>
          </button>
        )
      })}
    </div>
  )
}

// Use type assertion to properly type the forwardRef with generics
export const WizardList = React.forwardRef(WizardListInner) as <
  T extends WizardListItem
>(
  props: WizardListProps<T> & { ref?: React.ForwardedRef<HTMLDivElement> }
) => React.ReactElement
